// Agenda rules (docs/03 R17–R20). Agenda events are outside the strategic hierarchy: no weights, no review.
import { z } from "zod";
import { LIMITS } from "./limits.ts";
import { isIsoDate } from "./dates.ts";
import { optionalText, requiredText, uuid, version } from "./setup.ts";
import type { PendingField } from "./planning.ts";

const nullableDate = z
  .string()
  .nullish()
  .transform((v) => (v == null || v === "" ? null : v))
  .refine((v) => v === null || isIsoDate(v), "Informe uma data válida.");

export const taskSchema = z
  .object({
    id: uuid.nullish(),
    description: requiredText(LIMITS.taskDescription, "Descreva a tarefa."),
    starts_on: nullableDate,
    ends_on: nullableDate,
    sort_order: z.number().int().optional(),
  })
  .refine((t) => !t.starts_on || !t.ends_on || t.ends_on >= t.starts_on, { path: ["ends_on"], message: "O término não pode ser anterior ao início." });

const targetCount = z
  .union([z.number(), z.null(), z.literal("")])
  .optional()
  .transform((v) => (v === "" || v === undefined ? null : v))
  .refine((v) => v === null || (Number.isInteger(v) && v >= 0), "Informe um número inteiro de participantes (zero ou mais).");

const eventFields = {
  name: requiredText(LIMITS.name, "Informe o nome da ação."),
  editor_group_id: uuid,
  committee_name: optionalText(LIMITS.name),
  event_on: nullableDate,
  target_audience: optionalText(LIMITS.actionDescription),
  target_count: targetCount,
  tasks: z.array(taskSchema).max(LIMITS.collection, `Use no máximo ${LIMITS.collection} itens.`).default([]),
};

export const eventCreateSchema = z.object(eventFields);
export const eventSaveSchema = z.object({ expected_version: version, ...eventFields });

export interface CompletenessInput {
  committee_name: string | null;
  event_on: string | null;
  target_audience: string | null;
  target_count: number | null;
  tasks: { description: string | null; starts_on: string | null; ends_on: string | null }[];
}

/** Derived, never a workflow state (R19). Zero or null target count is pending (DEC resolutions). */
export function agendaCompleteness(e: CompletenessInput): { complete: boolean; pending_fields: PendingField[] } {
  const p: PendingField[] = [];
  if (!e.committee_name) p.push({ path: "committee_name", message: "Informe a comissão ou comitê responsável." });
  if (!e.event_on) p.push({ path: "event_on", message: "Informe a data do evento." });
  if (!e.target_audience) p.push({ path: "target_audience", message: "Informe o público-alvo." });
  if (!e.target_count || e.target_count <= 0) p.push({ path: "target_count", message: "Informe a quantidade prevista de participantes." });
  if (e.tasks.length === 0) p.push({ path: "tasks", message: "Cadastre pelo menos uma tarefa com datas." });
  e.tasks.forEach((t, i) => {
    if (!t.starts_on) p.push({ path: `tasks.${i}.starts_on`, message: "Informe o início da tarefa." });
    if (!t.ends_on) p.push({ path: `tasks.${i}.ends_on`, message: "Informe o término da tarefa." });
  });
  return { complete: p.length === 0, pending_fields: p };
}

export const COMPLETENESS_LABEL = { complete: "Cadastro completo", incomplete: "Cadastro em elaboração" } as const;

/** Shared by list and exports so both filter identically (docs/05 agenda routes). */
export const agendaFilterSchema = z.object({
  from: z.string().refine(isIsoDate, "Data inválida.").optional(),
  to: z.string().refine(isIsoDate, "Data inválida.").optional(),
  undated: z.enum(["include", "only"]).optional(),
  committee: z.string().trim().max(LIMITS.name).optional(),
  group_id: uuid.optional(),
});
export type AgendaFilter = z.infer<typeof agendaFilterSchema>;

export const MONTH_NAMES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

export interface CalendarYear<E> { year: number; months: { month: string; events: (E & { day: string })[] }[] }

/**
 * Event calendar (BLD-23, instructor feedback pt 13): every month from January to December of each year that has
 * events, empty months included, events sorted by day; undated events are returned apart.
 */
export function eventCalendar<E extends { name: string; event_on: string | null }>(events: E[]): { years: CalendarYear<E>[]; undated: E[] } {
  const dated = events.filter((e) => e.event_on).sort((a, b) => a.event_on!.localeCompare(b.event_on!) || a.name.localeCompare(b.name, "pt-BR"));
  const years = [...new Set(dated.map((e) => Number(e.event_on!.slice(0, 4))))].sort();
  return {
    years: years.map((year) => ({
      year,
      months: MONTH_NAMES.map((month, m) => ({
        month,
        events: dated
          .filter((e) => Number(e.event_on!.slice(0, 4)) === year && Number(e.event_on!.slice(5, 7)) === m + 1)
          .map((e) => ({ ...e, day: e.event_on!.slice(8, 10) })),
      })),
    })),
    undated: events.filter((e) => !e.event_on).sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
  };
}
